import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Stack,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  LinearProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  ViewWeek as ViewWeekIcon,
  CalendarMonth as ViewMonthIcon,
  CalendarViewMonth as ViewYearIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { MonSuiviPlanningEntry, SuiviPhaseStepWithProgress } from '../../../types/suivi';
import PlansButton from '../../../components/plans/PlansButton';
import { suiviAPI, planningAPI } from '../../../services/api';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';

type ViewMode = 'week' | 'month' | 'year';

// Time slots for the calendar (08:00 to 08:00 next day - 24 hours)
const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00',
  '20:00', '21:00', '22:00', '23:00', '00:00', '01:00',
  '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00'
];

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const DAYS_FR_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  en_cours: { bg: '#2196F3', text: '#fff', label: 'En cours' },
  termine: { bg: '#4CAF50', text: '#fff', label: 'Terminé' },
  bloque: { bg: '#f44336', text: '#fff', label: 'Bloqué' },
};

const MonSuivi: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [entries, setEntries] = useState<MonSuiviPlanningEntry[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<MonSuiviPlanningEntry | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Get week start and end dates
  const getWeekRange = (date: Date): { start: Date; end: Date } => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    const start = new Date(d);
    start.setDate(diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 5); // Saturday
    return { start, end };
  };

  // Fetch data based on view mode
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      let dateFrom: string | undefined;
      let dateTo: string | undefined;

      if (viewMode === 'week') {
        const { start, end } = getWeekRange(currentDate);
        dateFrom = start.toISOString().split('T')[0];
        dateTo = end.toISOString().split('T')[0];
      } else if (viewMode === 'month') {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        dateFrom = new Date(year, month, 1).toISOString().split('T')[0];
        dateTo = new Date(year, month + 1, 0).toISOString().split('T')[0];
      } else if (viewMode === 'year') {
        const year = currentDate.getFullYear();
        dateFrom = `${year}-01-01`;
        dateTo = `${year}-12-31`;
      }

      const response = await suiviAPI.getMonSuivi(undefined, dateFrom, dateTo, 0, 1000);
      setEntries(response.items);
    } catch (error) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Navigation functions
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setFullYear(newDate.getFullYear() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setFullYear(newDate.getFullYear() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get week dates
  const getWeekDates = (): Date[] => {
    const { start } = getWeekRange(currentDate);
    const dates: Date[] = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Get month calendar data
  const getMonthCalendarData = (): (Date | null)[][] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    const weeks: (Date | null)[][] = [];
    let currentWeek: (Date | null)[] = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      currentWeek.push(null);
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      currentWeek.push(new Date(year, month, day));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  };

  // Get entries for a specific date
  const getEntriesForDate = (date: Date): MonSuiviPlanningEntry[] => {
    const dateStr = date.toISOString().split('T')[0];
    return entries.filter(entry => entry.date === dateStr);
  };

  // Get entries for a specific date and time slot (week view)
  const getEntriesForSlot = (date: Date, slotTime: string): MonSuiviPlanningEntry[] => {
    const dateStr = date.toISOString().split('T')[0];
    return entries.filter(entry => {
      if (entry.date !== dateStr) return false;
      const entryStart = entry.start_time.substring(0, 5);
      const entryEnd = entry.end_time.substring(0, 5);
      // Check if slot time falls within the entry's time range
      // Handle overnight entries (e.g., 22:00-02:00)
      if (entryEnd <= entryStart) {
        // Overnight: slot is within if >= start OR < end
        return slotTime >= entryStart || slotTime < entryEnd;
      } else {
        // Normal: slot is within if >= start AND < end
        return slotTime >= entryStart && slotTime < entryEnd;
      }
    });
  };

  // Get entries for a specific month (year view)
  const getEntriesForMonth = (monthIndex: number): MonSuiviPlanningEntry[] => {
    const year = currentDate.getFullYear();
    return entries.filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate.getFullYear() === year && entryDate.getMonth() === monthIndex;
    });
  };

  // Handle entry click - open dialog to update progress
  const handleEntryClick = (entry: MonSuiviPlanningEntry) => {
    setSelectedEntry(entry);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEntry(null);
  };

  // Toggle step progress
  const handleToggleStep = async (step: SuiviPhaseStepWithProgress) => {
    if (!selectedEntry) return;

    const previousStatus = selectedEntry.status;

    try {
      await suiviAPI.toggleProgress(step.id, selectedEntry.projet_id, selectedEntry.service_id);
      // Refresh data
      await fetchData();
      // Refresh selected entry
      const updatedResponse = await suiviAPI.getMonSuivi(undefined, undefined, undefined, 0, 1000);
      const updatedEntry = updatedResponse.items.find(e => e.id === selectedEntry.id);
      if (updatedEntry) {
        setSelectedEntry(updatedEntry);
        // Check if status changed to "termine"
        if (previousStatus !== 'termine' && updatedEntry.status === 'termine') {
          showSuccess('Toutes les etapes sont terminees! Le statut est maintenant "Termine"');
        } else {
          showSuccess('Progression mise a jour');
        }
      } else {
        showSuccess('Progression mise a jour');
      }
    } catch (error) {
      showError(extractErrorMessage(error));
    }
  };

  // Mark planning entry as terminé manually
  const handleMarkAsTermine = async () => {
    if (!selectedEntry) return;

    try {
      await planningAPI.updateStatus(selectedEntry.id, 'termine');
      showSuccess('Statut mis a jour: Termine');
      // Refresh data
      await fetchData();
      // Refresh selected entry
      const updatedResponse = await suiviAPI.getMonSuivi(undefined, undefined, undefined, 0, 1000);
      const updatedEntry = updatedResponse.items.find(e => e.id === selectedEntry.id);
      if (updatedEntry) {
        setSelectedEntry(updatedEntry);
      }
    } catch (error) {
      showError(extractErrorMessage(error));
    }
  };

  const formatDateHeader = (date: Date, dayIndex: number): string => {
    const day = date.getDate();
    const month = date.toLocaleDateString('fr-FR', { month: 'short' });
    return `${DAYS_FR[dayIndex]} ${day} ${month}`;
  };

  const getNavigationLabel = (): string => {
    if (viewMode === 'week') {
      const { start, end } = getWeekRange(currentDate);
      return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    } else if (viewMode === 'month') {
      return `${MONTHS_FR[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else {
      return `${currentDate.getFullYear()}`;
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return '#4CAF50';
    if (percentage >= 50) return '#FF9800';
    return '#2196F3';
  };

  const weekDates = getWeekDates();
  const monthCalendarData = getMonthCalendarData();

  // Render entry chip/card
  const renderEntryChip = (entry: MonSuiviPlanningEntry, compact: boolean = false) => (
    <Tooltip
      key={entry.id}
      title={
        <Box>
          <Typography variant="body2">
            {entry.projet_code ? `Projet: ${entry.projet_code}` : `Service: ${entry.service_code}`}
          </Typography>
          <Typography variant="body2">
            {entry.projet_nom || entry.service_nom}
          </Typography>
          <Typography variant="body2">
            Atelier: {entry.atelier_name}
          </Typography>
          <Typography variant="body2">
            {entry.start_time} - {entry.end_time}
          </Typography>
          <Typography variant="body2">
            Progression: {entry.overall_progress_percentage.toFixed(0)}%
          </Typography>
          {!!entry.plans && <Typography variant="body2">Plans techniques : {entry.plans}</Typography>}
        </Box>
      }
      arrow
    >
      <Box
        sx={{
          bgcolor: STATUS_COLORS[entry.status]?.bg || '#2196F3',
          color: STATUS_COLORS[entry.status]?.text || '#fff',
          p: compact ? 0.25 : 0.5,
          borderRadius: 1,
          mb: 0.5,
          fontSize: compact ? '0.65rem' : '0.75rem',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            opacity: 0.9,
            transform: 'scale(1.02)',
          },
          transition: 'all 0.2s',
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleEntryClick(entry);
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', fontSize: compact ? '0.6rem' : '0.75rem' }}>
          {entry.projet_code || entry.service_code}
        </Typography>
        {!compact && (
          <>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem' }}>
              {entry.atelier_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={entry.overall_progress_percentage}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white',
                    borderRadius: 2,
                  },
                }}
              />
              <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                {entry.overall_progress_percentage.toFixed(0)}%
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Tooltip>
  );

  // Render Week View
  const renderWeekView = () => (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ width: 'fit-content' }}>
        {/* Header Row - Time slots */}
        <Grid container sx={{ borderBottom: 2, borderColor: 'divider', flexWrap: 'nowrap' }}>
          <Grid item sx={{ width: 120, minWidth: 120, p: 1, bgcolor: 'background.default', borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight="bold">Jour</Typography>
          </Grid>
          {TIME_SLOTS.slice(0, -1).map((timeSlot, index) => (
            <Grid
              item
              key={timeSlot}
              sx={{
                width: 85,
                minWidth: 85,
                p: 0.5,
                bgcolor: 'background.default',
                borderRight: 1, borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                {timeSlot}-{TIME_SLOTS[index + 1]}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Day Rows */}
        {weekDates.map((date, dayIndex) => (
          <Grid container key={dayIndex} sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 70, flexWrap: 'nowrap' }}>
            <Grid
              item
              sx={{
                width: 120,
                minWidth: 120,
                p: 1,
                bgcolor: 'background.default',
                borderRight: 1, borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.75rem' }}>
                {formatDateHeader(date, dayIndex)}
              </Typography>
            </Grid>
            {TIME_SLOTS.slice(0, -1).map((timeSlot, timeIndex) => {
              const slotEntries = getEntriesForSlot(date, timeSlot);
              return (
                <Grid
                  item
                  key={timeSlot}
                  sx={{
                    width: 85,
                    minWidth: 85,
                    p: 0.25,
                    borderRight: 1, borderColor: 'divider',
                    position: 'relative',
                    minHeight: 70,
                  }}
                >
                  {slotEntries.map((entry) => renderEntryChip(entry))}
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Box>
    </Box>
  );

  // Render Month View
  const renderMonthView = () => (
    <Box>
      {/* Days header */}
      <Grid container sx={{ borderBottom: 2, borderColor: 'divider' }}>
        {DAYS_FR_SHORT.map((day, index) => (
          <Grid
            item
            key={day}
            sx={{
              flex: 1,
              p: 1,
              bgcolor: 'background.default',
              borderRight: index < 6 ? 1 : 0, borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">{day}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar weeks */}
      {monthCalendarData.map((week, weekIndex) => (
        <Grid container key={weekIndex} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {week.map((date, dayIndex) => {
            const dayEntries = date ? getEntriesForDate(date) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();
            return (
              <Grid
                item
                key={dayIndex}
                sx={{
                  flex: 1,
                  minHeight: 100,
                  p: 0.5,
                  borderRight: dayIndex < 6 ? 1 : 0, borderColor: 'divider',
                  bgcolor: date ? (isToday ? 'rgba(255, 87, 34, 0.1)' : 'background.paper') : 'background.default',
                }}
              >
                {date && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isToday ? 'bold' : 'normal',
                        color: isToday ? '#FF5722' : 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                    <Box sx={{ maxHeight: 70, overflow: 'auto' }}>
                      {dayEntries.slice(0, 3).map((entry) => renderEntryChip(entry, true))}
                      {dayEntries.length > 3 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          +{dayEntries.length - 3} autres
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );

  // Render Year View
  const renderYearView = () => (
    <Grid container spacing={2}>
      {MONTHS_FR.map((month, monthIndex) => {
        const monthEntries = getEntriesForMonth(monthIndex);
        const totalProgress = monthEntries.length > 0
          ? monthEntries.reduce((sum, e) => sum + e.overall_progress_percentage, 0) / monthEntries.length
          : 0;

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={month}>
            <Paper
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'rgba(255, 87, 34, 0.05)',
                  boxShadow: 3,
                },
                transition: 'all 0.2s',
              }}
              onClick={() => {
                const newDate = new Date(currentDate.getFullYear(), monthIndex, 1);
                setCurrentDate(newDate);
                setViewMode('month');
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#FF5722' }}>
                {month}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {monthEntries.length} tache{monthEntries.length !== 1 ? 's' : ''}
              </Typography>
              {monthEntries.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={totalProgress}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'divider',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getProgressColor(totalProgress),
                        borderRadius: 4,
                      },
                    }}
                  />
                  <Typography variant="body2" fontWeight="medium">
                    {totalProgress.toFixed(0)}%
                  </Typography>
                </Box>
              )}

              {/* Mini list of entries */}
              {monthEntries.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
                  {monthEntries.slice(0, 3).map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        borderBottom: 1, borderColor: 'divider',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEntryClick(entry);
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: STATUS_COLORS[entry.status]?.bg || '#2196F3',
                        }}
                      />
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                        {entry.projet_code || entry.service_code} - {new Date(entry.date).getDate()}/{monthIndex + 1}
                      </Typography>
                    </Box>
                  ))}
                  {monthEntries.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{monthEntries.length - 3} autres
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5">
            Mon Suivi
          </Typography>

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newValue) => newValue && setViewMode(newValue)}
            size="small"
          >
            <ToggleButton value="week">
              <ViewWeekIcon sx={{ mr: 0.5 }} /> Semaine
            </ToggleButton>
            <ToggleButton value="month">
              <ViewMonthIcon sx={{ mr: 0.5 }} /> Mois
            </ToggleButton>
            <ToggleButton value="year">
              <ViewYearIcon sx={{ mr: 0.5 }} /> Annee
            </ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={goToPrevious}>
              <ChevronLeftIcon />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={goToToday}
            >
              Aujourd'hui
            </Button>
            <IconButton onClick={goToNext}>
              <ChevronRightIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minWidth: 250, textAlign: 'center' }}>
              {getNavigationLabel()}
            </Typography>
          </Stack>
        </Stack>

        {/* Legend */}
        <Stack direction="row" spacing={2} mb={2}>
          {Object.entries(STATUS_COLORS).map(([status, colors]) => (
            <Chip
              key={status}
              label={colors.label}
              size="small"
              sx={{ bgcolor: colors.bg, color: colors.text }}
            />
          ))}
        </Stack>

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2, bgcolor: '#FFCCBC', '& .MuiLinearProgress-bar': { bgcolor: '#FF5722' } }} />}

        {/* Calendar View */}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'year' && renderYearView()}

        {/* Empty state */}
        {!loading && entries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              Aucune tache assignee pour cette periode
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Progress Update Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Mise a jour de la progression
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box sx={{ mt: 2 }}>
              {/* Entry Info */}
              <Card sx={{ mb: 2, bgcolor: 'background.default' }}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Code</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedEntry.projet_code || selectedEntry.service_code}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Atelier</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedEntry.atelier_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Date</Typography>
                      <Typography variant="body2">
                        {new Date(selectedEntry.date).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Horaire</Typography>
                      <Typography variant="body2">
                        {selectedEntry.start_time} - {selectedEntry.end_time}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Nom</Typography>
                      <Typography variant="body2">
                        {selectedEntry.projet_nom || selectedEntry.service_nom}
                      </Typography>
                    </Grid>
                    {(selectedEntry.responsables?.length ?? 0) > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedEntry.responsables?.length ?? 0) > 1 ? 'Responsables' : 'Responsable'}
                        </Typography>
                        <Typography variant="body2">
                          {(selectedEntry.responsables || [])
                            .map(r => r.full_name)
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Grid>
                    )}
                    {!!selectedEntry.plans && (
                      <Grid item xs={12}>
                        <PlansButton count={selectedEntry.plans} title={`${selectedEntry.projet_code || selectedEntry.service_code} · ${selectedEntry.atelier_name}`}
                          load={() => suiviAPI.monSuiviPlans(selectedEntry.id)} color="#FF5722" />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Progression Globale</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={selectedEntry.status === 'termine' ? 100 : selectedEntry.overall_progress_percentage}
                          sx={{
                            flex: 1,
                            height: 10,
                            borderRadius: 5,
                            bgcolor: 'divider',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: selectedEntry.status === 'termine' ? '#4CAF50' : getProgressColor(selectedEntry.overall_progress_percentage),
                              borderRadius: 5,
                            },
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {selectedEntry.status === 'termine' ? '100' : selectedEntry.overall_progress_percentage.toFixed(0)}%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Phase Steps */}
              {selectedEntry.phase ? (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {selectedEntry.phase.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedEntry.phase.steps.map((step) => (
                      <Box
                        key={step.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: step.is_completed ? '#e8f5e9' : 'background.paper',
                          border: '1px solid',
                          borderColor: step.is_completed ? '#4CAF50' : 'divider',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: step.is_completed ? '#c8e6c9' : 'background.default',
                          },
                        }}
                        onClick={() => handleToggleStep(step)}
                      >
                        <Checkbox
                          checked={step.is_completed}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggleStep(step)}
                          sx={{
                            color: '#FF5722',
                            '&.Mui-checked': { color: '#4CAF50' },
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              textDecoration: step.is_completed ? 'line-through' : 'none',
                              color: step.is_completed ? 'text.secondary' : 'text.primary',
                            }}
                          >
                            {step.name}
                          </Typography>
                          {step.is_completed && step.completed_at && (
                            <Typography variant="caption" color="text.secondary">
                              Complete le {new Date(step.completed_at).toLocaleDateString('fr-FR')}
                              {step.completed_by_name && ` par ${step.completed_by_name}`}
                            </Typography>
                          )}
                        </Box>
                        {step.is_completed && (
                          <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Aucune phase configuree pour cet atelier
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            onClick={handleMarkAsTermine}
            variant="contained"
            startIcon={<CheckCircleIcon />}
            disabled={selectedEntry?.status === 'termine'}
            sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' }, '&.Mui-disabled': { bgcolor: '#A5D6A7', color: '#fff' } }}
          >
            {selectedEntry?.status === 'termine' ? 'Deja Termine' : 'Marquer comme Termine'}
          </Button>
          <Button onClick={handleCloseDialog} variant="contained" sx={{ bgcolor: '#FF5722', '&:hover': { bgcolor: '#E64A19' } }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default MonSuivi;
